import Web3 from 'web3'
import { Address, Wei } from '../../utils/types'
import { Factory, Pool } from '../../abi/abi'
import { Pool as IPool } from '../../logic/pools/Pool'
import { toWei } from '../../utils/static-web3'
import { approveAmount, TokenDefinition, protocolPrecision } from '../../config'
import { TokenApi } from './token-api'
import { Token } from '../../logic/token/token'

export class PoolApi {
  contract
  constructor(
    public web3: Web3,
    public account: Address,
    public poolAddress: Address
  ) {
    // @ts-ignore
    this.contract = new this.web3.eth.Contract(Pool, this.poolAddress)
  }

  static async createPool(
    web3: Web3,
    account: Address,
    factoryAddress: Address
  ) {
    // @ts-ignore
    const factoryContract = new web3.eth.Contract(Factory, factoryAddress)
    return factoryContract.methods.newPool().send({ from: account })
  }

  async allowance(pool: IPool, other: Address): Promise<Map<Token, Wei>> {
    const map = new Map<Token, Wei>()

    const tokens = pool.tokens
    const promises: Promise<[Token, Wei]>[] = []

    tokens.forEach(token => {
      const tokenApi = new TokenApi(this.web3, this.account, token)
      promises.push(
        tokenApi.allowance(other).then(allowed => {
          return [token, allowed]
        })
      )
    })
    const result = await Promise.all(promises)
    result.forEach(([token, allowed]) => map.set(token, allowed))
    return map
  }

  approveToken(token: Token, max: Wei = approveAmount) {
    const tokenApi = new TokenApi(this.web3, this.account, token)
    return tokenApi.allow(this.poolAddress, max)
  }

  async bindPool(token: TokenDefinition, amount: number, oracle: Address) {
    // @ts-ignore
    return this.contract.methods
      .bindMMM(
        token.address,
        toWei(amount, token.decimals),
        toWei(5, protocolPrecision),
        oracle
      )
      .send({
        from: this.account,
        maxPriorityFeePerGas: null,
        maxFeePerGas: null,
      })
  }

  async finalize() {
    return this.contract.methods.finalize().send({
      from: this.account,
      maxPriorityFeePerGas: null,
      maxFeePerGas: null,
    })
  }

  async getTokensInPoolOrder(): Promise<Token[]> {
    return this.contract.methods.getFinalTokens().call({ from: this.account })
  }

  async viewOneAssetJoinShare(token: Token, amount: Wei): Promise<Wei> {
    return await this.contract.methods
      .getJoinswapExternAmountInMMM(token.address, amount)
      .call({ from: this.account })
  }

  /**
   * Before executing the joinWithOneAsset call, check if the pool would be balanced
   * return amount if would be balanced and call is ok, return false if impossible to join
   * @param token
   * @param amount
   */
  async checkBalanced(token: Token, amount: Wei): Promise<Wei | false> {
    try {
      const poolAmount = await this.contract.methods
        .getJoinswapExternAmountInMMM(token.address, amount)
        .call({ from: this.account })
      return poolAmount
    } catch (e: any) {
      // TODO: complete with good values
      console.debug('TODO : case with IMBALANCE', e)
      if (e.code === 6) {
        return false
      }
      return false
    }
  }

  async viewMultiAssetJoinShare(
    token: Token,
    amount: Wei
  ): Promise<[Wei, Wei[]]> {
    const result = await this.contract.methods
      .getJoinPool(token.address, amount)
      .call({ from: this.account })
    // result[0] : exact poolAmountOut of pool share
    // result[1] : amountsIn for each kind of token
    return [result[0], result[1]]
  }

  async joinWithOneAsset(token: Token, amount: Wei, maximum: Wei) {
    return await this.contract.methods
      .joinswapExternAmountInMMM(token.address, amount, maximum)
      .send({
        from: this.account,
        maxPriorityFeePerGas: null,
        maxFeePerGas: null,
      })
  }
}
